import { NgClass } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import type { MenuItem } from '@openng/optimus-ui/api';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { ListboxModule } from '@openng/optimus-ui/listbox';
import { MenuModule } from '@openng/optimus-ui/menu';
import { MessageModule } from '@openng/optimus-ui/message';
import { type Popover, PopoverModule } from '@openng/optimus-ui/popover';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { injectParams } from 'ngxtension/inject-params';
import { combineLatest } from 'rxjs';

import { FieldErrorComponent } from '@app/shared/components/field-error/field-error.component';
import { InputContainerComponent } from '@app/shared/components/input-container/input-container.component';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton.component';
import { SKELETON_TYPES } from '@app/shared/constants/shared.constant';
import {
    getProjectForAPI,
    getProjectForm,
    type ProjectAdapter,
} from '@app/shared/models/project.model';
import { PermissionService } from '@app/shared/services/permission.service';
import { ProjectService } from '@app/shared/services/project.service';
import { StorageService } from '@app/shared/services/storage.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { FormHelper } from '@app/shared/utils/form-helper';

interface ProjectOption {
    id: number;
    name: string;
    slug?: string;
    description: string;
    project: ProjectAdapter;
    menuItems: MenuItem[];
}

export const PROJECT_SLUG_STORAGE_KEY = 'selected-project-slug';

@Component({
    selector: 'cor-project-list',
    exportAs: 'corProjectList',
    templateUrl: './project-list.component.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        ListboxModule,
        MenuModule,
        PopoverModule,
        DialogModule,
        InputTextModule,
        TextareaModule,
        FieldErrorComponent,
        InputContainerComponent,
        SkeletonComponent,
        ButtonModule,
        NgClass,
        MessageModule,
        TooltipModule,
    ],
})
export class ProjectListComponent {
    protected readonly currentProject = injectParams<string>('projectSlug');

    readonly isHomePage = input(false);
    readonly projectSelected = output<string | null>();

    // This signal is populated from currentProject and localStorage (if isHomePage=true)
    protected readonly selectedProjectSlug = signal<string | null>(null);

    protected readonly projectPopover = viewChild<Popover>('projectPopover');

    private readonly destroyRef = inject(DestroyRef);
    private readonly projectService = inject(ProjectService);
    private readonly permissionService = inject(PermissionService);
    private readonly toast = inject(ToastNotificationService);
    protected readonly storageService = inject(StorageService);

    protected readonly SKELETON_TYPES = SKELETON_TYPES;

    protected readonly project = {
        isLoading: signal(false),
        list: signal<ProjectAdapter[]>([]),
        activePopover: signal<Popover | null>(null),
        options: computed((): ProjectOption[] =>
            this.project.list().map(project => ({
                id: project.id,
                name: project.name,
                slug: project.slug,
                description: project.description,
                project,
                menuItems: [
                    {
                        label: 'Edit',
                        icon: 'pi pi-pencil',
                        command: () => {
                            this.project.activePopover()?.hide();
                            this.project.dialog.onShow(project);
                        },
                    },
                    {
                        label: 'Delete',
                        icon: 'pi pi-trash tw:text-(--p-red-500)',
                        labelClass: 'tw:text-(--p-red-500)',
                        command: () => {
                            this.project.activePopover()?.hide();
                            this.project.onDelete(project);
                        },
                    },
                ],
            })),
        ),
        loadProjects: () => {
            this.project.isLoading.set(true);
            this.projectService
                .getAll()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(({ result }) => {
                    this.project.list.set(result);
                })
                .add(() => this.project.isLoading.set(false));
        },
        onSelect: (slug: string | null) => {
            this.selectedProjectSlug.set(slug);
            this.permissionService.setSelectedProject(slug);
            this.storeProjectSlugInStorage(slug);
            this.projectSelected.emit(slug);
        },
        onDelete: (project: ProjectAdapter) => {
            this.project.deleteDialog.onShow(project);
        },
        deleteDialog: {
            isVisible: signal(false),
            isDeleting: signal(false),
            item: signal<ProjectAdapter | null>(null),
            confirmationInput: signal(''),
            isDeleteConfirmed: computed((): boolean => {
                const project = this.project.deleteDialog.item();
                return (
                    !!project &&
                    this.project.deleteDialog.confirmationInput().toLowerCase().trim() ===
                        project.name.toLowerCase().trim()
                );
            }),
            onShow: (project: ProjectAdapter) => {
                this.project.deleteDialog.item.set(project);
                this.project.deleteDialog.confirmationInput.set('');
                this.project.deleteDialog.isVisible.set(true);
            },
            onHide: () => {
                this.project.deleteDialog.isDeleting.set(false);
                this.project.deleteDialog.isVisible.set(false);
                this.project.deleteDialog.confirmationInput.set('');
                this.project.deleteDialog.item.set(null);
            },
            onDelete: () => {
                const project = this.project.deleteDialog.item();
                if (
                    !project ||
                    !this.project.deleteDialog.isDeleteConfirmed() ||
                    this.project.deleteDialog.isDeleting()
                ) {
                    return;
                }

                this.project.deleteDialog.isDeleting.set(true);
                this.projectService
                    .delete(project.id)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        this.toast.showSuccess('Project deleted successfully');
                        this.project.list.update(projects =>
                            projects.filter(currentProject => currentProject.id !== project.id),
                        );
                        this.project.onSelect(null);
                    })
                    .add(() => {
                        this.project.deleteDialog.onHide();
                    });
            },
        },
        dialog: {
            isVisible: signal(false),
            isSaving: signal(false),
            form: getProjectForm({} as ProjectAdapter),
            onShow: (item?: ProjectAdapter) => {
                this.project.dialog.form.reset(item ?? {});
                this.project.dialog.form.markAsPristine();
                this.project.dialog.form.markAsUntouched();
                this.project.dialog.isVisible.set(true);
            },
            onClose: () => {
                if (
                    this.project.dialog.form.dirty &&
                    !window.confirm(
                        'You have unsaved changes. Are you sure you want to leave this page?',
                    )
                ) {
                    return;
                }

                this.project.dialog.form.reset({});
                FormHelper.markFormAsPristine(this.project.dialog.form);
                this.project.dialog.isVisible.set(false);
            },
            onSave: () => {
                if (this.project.dialog.form.invalid) {
                    FormHelper.showFormErrors(this.project.dialog.form, this.toast);
                    return;
                }

                this.project.dialog.isSaving.set(true);
                const payload = getProjectForAPI(this.project.dialog.form.getRawValue());
                (payload.id
                    ? this.projectService.update(payload)
                    : this.projectService.create(payload)
                )
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(({ result }) => {
                        this.toast.showSuccess(
                            payload.id
                                ? 'Project updated successfully'
                                : 'Project created successfully',
                        );

                        this.project.list.update(projects =>
                            payload.id
                                ? projects.map(project =>
                                      project.id === payload.id ? result : project,
                                  )
                                : [...projects, result],
                        );
                        FormHelper.markFormAsPristine(this.project.dialog.form);
                        this.project.onSelect(result.slug);
                        this.project.dialog.onClose();
                    })
                    .add(() => {
                        this.project.dialog.isSaving.set(false);
                    });
            },
        },
    };

    readonly selectedProject = computed(
        (): ProjectAdapter =>
            this.project.list().find(project => project.slug === this.selectedProjectSlug()),
    );

    constructor() {
        combineLatest({
            currentProject: toObservable(this.currentProject),
            isHomePage: toObservable(this.isHomePage),
        }).subscribe(({ currentProject, isHomePage }) => {
            if (!isHomePage) {
                this.selectedProjectSlug.set(currentProject);
                this.permissionService.setSelectedProject(currentProject);
                this.storeProjectSlugInStorage(currentProject);
            } else {
                const storedSlug = this.storageService.getLocalStorage(PROJECT_SLUG_STORAGE_KEY);
                this.selectedProjectSlug.set(storedSlug);
                this.permissionService.setSelectedProject(storedSlug);
            }
        });
    }

    private storeProjectSlugInStorage(slug: string | null) {
        if (slug) {
            this.storageService.setLocalStorage(PROJECT_SLUG_STORAGE_KEY, slug);
        } else {
            this.storageService.clearKeyFromLocalStorage(PROJECT_SLUG_STORAGE_KEY);
        }
    }

    ngOnInit() {
        this.project.loadProjects();
    }

    toggle(event: Event) {
        this.projectPopover()?.toggle(event);
    }

    hide() {
        this.projectPopover()?.hide();
    }
}
